import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useOrder } from '../context/OrderContext';
import { submitQrOrder } from '../services/firebase';
import { getOrderRequestId, clearOrderRequestId } from '../utils/orderRequestId';
import { currentBranchId } from '../config/branchContext';
import { useLanguage } from '../context/LanguageContext';
import { useBrand } from '../context/BrandContext';

const CartPage: React.FC = () => {
  const { tableNumber } = useParams<{ tableNumber: string }>();
  const navigate = useNavigate();
  const { items, removeItem, updateQuantity, clearCart, total, itemCount } = useCart();
  const { mode, tableNumber: orderTable } = useOrder();
  const { lang, setLang, t } = useLanguage();
  const { brand } = useBrand();
  const [orderNotes, setOrderNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isDineIn = mode === 'dine_in' && (tableNumber || orderTable !== '0');
  const displayTable = tableNumber || orderTable;

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price);

  // Dine-in: submit directly (legacy QR flow). Delivery/pickup: go to checkout page.
  const handleProceed = async () => {
    if (items.length === 0) return;

    if (!isDineIn) {
      // Go to checkout for delivery/pickup
      navigate('/checkout');
      return;
    }

    // Dine-in QR flow — submit directly (original behavior)
    setSubmitting(true);
    setError('');
    try {
      const orderItems = items.map((item) => ({
        menu_item_id: item.menuItemId,
        name: item.name,
        name_de: item.name_de,
        price: item.price,
        quantity: item.quantity,
        category: item.categoryName,
        notes: item.notes,
        addons: item.addons.map((a) => ({ id: a.id, name: a.name, name_de: a.name_de, price: a.price })),
      }));

      const payload: Record<string, any> = {
        tableNumber: parseInt(displayTable || '0', 10),
        items: orderItems,
        notes: orderNotes,
        total,
        source: 'qr_order',
        branch_id: currentBranchId(),
      };
      // Same cart ⇒ same id, so a retry after a timeout can't add the round twice.
      payload.client_request_id = getOrderRequestId(payload);
      const result = await submitQrOrder(payload);

      const data = result.data as any;
      const orderNumber = data.order_number || data.orderNumber || data.order_id || '';
      clearCart();
      clearOrderRequestId();
      navigate(`/table/${displayTable}/confirmed/${orderNumber}`);
    } catch (err: any) {
      console.error('Order submission error:', err);
      setError(err.message || t('submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const backPath = isDineIn ? `/table/${displayTable}/menu/${brand.id}` : -1;

  return (
    <div className="page cart-page cart-page-v2">
      <header className="menu-header">
        <button className="back-btn" onClick={() => typeof backPath === 'string' ? navigate(backPath) : navigate(-1)}>&#8592;</button>
        <div className="header-info">
          <span className="header-brand">{t('cart')}</span>
          {isDineIn && <span className="header-table">{t('table')} {displayTable}</span>}
          {!isDineIn && <span className="header-table">{mode === 'delivery' ? t('delivery') : t('pickup')}</span>}
        </div>
        <div className="lang-toggle" style={{ position: 'relative', top: 'auto', right: 'auto', marginLeft: 'auto' }}>
          <button className={lang === 'de' ? 'active' : ''} onClick={() => setLang('de')}>DE</button>
          <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')}>EN</button>
        </div>
      </header>

      {items.length === 0 ? (
        <div className="empty-cart">
          <p>{t('emptyCart')}</p>
          <button className="btn-primary" onClick={() => navigate(isDineIn ? `/table/${displayTable}` : '/')}>
            {t('backToMenu')}
          </button>
        </div>
      ) : (
        <div className="cart-grid">
          <div className="cart-items-col">
            <div className="cart-items">
              {items.map((item) => {
                const addonTotal = item.addons.reduce((s, a) => s + a.price, 0);
                const lineTotal = (item.price + addonTotal) * item.quantity;
                return (
                  <div key={item.cartId} className="cart-item">
                    <div className="cart-item-top">
                      <div className="cart-item-info">
                        <span className="cart-item-name">{item.name_de || item.name}</span>
                        {item.addons.length > 0 && (
                          <span className="cart-item-addons">+ {item.addons.map((a) => a.name_de || a.name).join(', ')}</span>
                        )}
                        {item.notes && <span className="cart-item-notes">{item.notes}</span>}
                      </div>
                      <span className="cart-item-price">{formatPrice(lineTotal)}</span>
                    </div>
                    <div className="cart-item-actions">
                      <div className="quantity-row compact">
                        <button className="qty-btn small"
                          onClick={() => item.quantity <= 1 ? removeItem(item.cartId) : updateQuantity(item.cartId, item.quantity - 1)}>
                          {item.quantity <= 1 ? '🗑' : '−'}
                        </button>
                        <span className="qty-value">{item.quantity}</span>
                        <button className="qty-btn small" onClick={() => updateQuantity(item.cartId, item.quantity + 1)}>+</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {isDineIn && (
              <div className="order-notes-section">
                <label htmlFor="order-notes">{t('notes')}</label>
                <textarea id="order-notes" placeholder={t('notesPlaceholder')}
                  value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)} rows={2} />
              </div>
            )}
          </div>

          <div className="cart-summary-col">
            <div className="cart-total">
              <span>{t('total')} ({itemCount} {t('items')})</span>
              <span className="total-price">{formatPrice(total)}</span>
            </div>

            {error && <p className="error-text">{error}</p>}

            <button className="btn-primary submit-btn" onClick={handleProceed} disabled={submitting}>
              {isDineIn
                ? (submitting ? t('submitting') : t('placeOrder'))
                : t('goToCheckout')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;
